(() => {
  if (customElements.get('ots-definition-popover')) return;

  const FINE_POINTER_QUERY = '(hover: hover) and (pointer: fine)';

  class OtsDefinitionPopover extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.trigger = this.querySelector('[data-ots-definition-trigger]');
      this.panel = this.querySelector('[data-ots-definition-panel]');
      this.closeButton = this.querySelector('[data-ots-definition-close]');
      if (!this.trigger || !this.panel || !this.closeButton) return;

      this.initialized = true;
      this.pinned = false;
      this.closeTimer = null;
      this.positionFrame = null;
      this.suppressNextFocusOpen = false;
      this.conditionPanel = this.closest('.ots-condition-panel');
      this.usesNativePopover =
        typeof this.panel.showPopover === 'function' && typeof this.panel.hidePopover === 'function';
      this.onTriggerClick = this.handleTriggerClick.bind(this);
      this.onPointerEnter = this.handlePointerEnter.bind(this);
      this.onPointerLeave = this.handlePointerLeave.bind(this);
      this.onFocusIn = this.handleFocusIn.bind(this);
      this.onFocusOut = this.handleFocusOut.bind(this);
      this.onCloseClick = this.handleCloseClick.bind(this);
      this.onDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
      this.onDocumentKeyDown = this.handleDocumentKeyDown.bind(this);
      this.onPopoverToggle = this.handlePopoverToggle.bind(this);
      this.onViewportChange = this.schedulePosition.bind(this);

      this.trigger.addEventListener('click', this.onTriggerClick);
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.closeButton.addEventListener('click', this.onCloseClick);
      this.panel.addEventListener('toggle', this.onPopoverToggle);
      document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
      document.addEventListener('keydown', this.onDocumentKeyDown);
      window.addEventListener('resize', this.onViewportChange);
      document.addEventListener('scroll', this.onViewportChange, true);
    }

    disconnectedCallback() {
      window.clearTimeout(this.closeTimer);
      window.cancelAnimationFrame(this.positionFrame);
      this.trigger?.removeEventListener('click', this.onTriggerClick);
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.closeButton?.removeEventListener('click', this.onCloseClick);
      this.panel?.removeEventListener('toggle', this.onPopoverToggle);
      document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
      document.removeEventListener('keydown', this.onDocumentKeyDown);
      window.removeEventListener('resize', this.onViewportChange);
      document.removeEventListener('scroll', this.onViewportChange, true);
      this.pinned = false;
      this.suppressNextFocusOpen = false;
      this.panel?.removeAttribute('data-open');
      this.panel?.removeAttribute('data-positioned');
      this.panel?.style.removeProperty('--ots-definition-popover-left');
      this.panel?.style.removeProperty('--ots-definition-popover-top');
      this.trigger?.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      this.conditionPanel?.classList.remove('ots-condition-panel--popover-fallback-open');
      this.initialized = false;
    }

    get isOpen() {
      return this.trigger?.getAttribute('aria-expanded') === 'true';
    }

    get supportsHover() {
      return window.matchMedia(FINE_POINTER_QUERY).matches;
    }

    handleTriggerClick(event) {
      // JS owns the toggle so focus-triggered and declarative popover actions do not race.
      event.preventDefault();
      this.clearScheduledClose();
      if (this.isOpen && this.pinned) {
        this.closePopover({ returnFocus: true });
        return;
      }

      this.pinned = true;
      this.openPopover();
      requestAnimationFrame(() => this.closeButton?.focus({ preventScroll: true }));
    }

    handlePointerEnter() {
      if (!this.supportsHover) return;
      this.clearScheduledClose();
      this.openPopover();
    }

    handlePointerLeave() {
      if (!this.supportsHover || this.pinned) return;
      this.closeTimer = window.setTimeout(() => {
        if (!this.contains(document.activeElement)) this.closePopover();
      }, 120);
    }

    handleFocusIn(event) {
      // Native popovers may restore focus while hidePopover() is still completing.
      // Ignore that entire focus-in turn so it cannot immediately reopen the
      // panel and raise InvalidStateError during the native hide operation.
      if (this.suppressNextFocusOpen) {
        this.suppressNextFocusOpen = false;
        return;
      }
      this.clearScheduledClose();
      this.openPopover();
    }

    handleFocusOut() {
      window.setTimeout(() => {
        if (!this.contains(document.activeElement)) this.closePopover();
      }, 0);
    }

    handleCloseClick(event) {
      event.preventDefault();
      this.closePopover({ returnFocus: true });
    }

    handleDocumentPointerDown(event) {
      if (!this.isOpen || this.contains(event.target)) return;
      this.closePopover();
    }

    handleDocumentKeyDown(event) {
      if (event.key !== 'Escape' || !this.isOpen) return;
      event.preventDefault();
      this.closePopover({ returnFocus: true });
    }

    handlePopoverToggle(event) {
      const opened = event.newState ? event.newState === 'open' : this.isNativePopoverOpen();
      if (opened) {
        this.applyOpenState();
        this.schedulePosition();
      } else {
        this.applyClosedState();
      }
    }

    openPopover() {
      if (this.isOpen) return;

      document.querySelectorAll('ots-definition-popover').forEach((popover) => {
        if (popover !== this && typeof popover.closePopover === 'function') popover.closePopover();
      });

      if (this.usesNativePopover) {
        if (!this.isNativePopoverOpen()) this.panel.showPopover();
      }
      this.applyOpenState();
      this.schedulePosition();
    }

    closePopover({ returnFocus = false } = {}) {
      if (!this.isOpen) return;

      this.clearScheduledClose();
      this.pinned = false;
      const suppressFocusOpen = returnFocus || this.panel.contains(document.activeElement);
      if (suppressFocusOpen) this.suppressNextFocusOpen = true;
      if (this.usesNativePopover && this.isNativePopoverOpen()) this.panel.hidePopover();
      this.applyClosedState();
      if (returnFocus && document.contains(this.trigger)) {
        this.trigger.focus({ preventScroll: true });
      }
      if (suppressFocusOpen) {
        requestAnimationFrame(() => {
          this.suppressNextFocusOpen = false;
        });
      }
    }

    isNativePopoverOpen() {
      return this.usesNativePopover && this.panel.matches(':popover-open');
    }

    applyOpenState() {
      this.panel.setAttribute('data-open', '');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
      if (!this.usesNativePopover) {
        this.conditionPanel?.classList.add('ots-condition-panel--popover-fallback-open');
      }
    }

    applyClosedState() {
      this.pinned = false;
      this.panel.removeAttribute('data-open');
      this.panel.removeAttribute('data-positioned');
      this.panel.style.removeProperty('--ots-definition-popover-left');
      this.panel.style.removeProperty('--ots-definition-popover-top');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      this.conditionPanel?.classList.remove('ots-condition-panel--popover-fallback-open');
    }

    schedulePosition() {
      if (!this.isOpen || this.positionFrame) return;
      this.positionFrame = requestAnimationFrame(() => {
        this.positionFrame = null;
        this.positionPopover();
      });
    }

    positionPopover() {
      if (!this.isOpen || window.matchMedia('(max-width: 749px)').matches) {
        this.panel.removeAttribute('data-positioned');
        this.panel.style.removeProperty('--ots-definition-popover-left');
        this.panel.style.removeProperty('--ots-definition-popover-top');
        return;
      }

      const edge = 12;
      const gap = 8;
      const triggerRect = this.trigger.getBoundingClientRect();
      const panelRect = this.panel.getBoundingClientRect();
      const maximumLeft = Math.max(edge, window.innerWidth - panelRect.width - edge);
      const left = Math.min(Math.max(triggerRect.left, edge), maximumLeft);
      let top = triggerRect.bottom + gap;

      if (top + panelRect.height > window.innerHeight - edge) {
        const flippedTop = triggerRect.top - panelRect.height - gap;
        top = flippedTop >= edge ? flippedTop : Math.max(edge, window.innerHeight - panelRect.height - edge);
      }

      this.panel.style.setProperty('--ots-definition-popover-left', `${Math.round(left)}px`);
      this.panel.style.setProperty('--ots-definition-popover-top', `${Math.round(top)}px`);
      this.panel.setAttribute('data-positioned', '');
    }

    clearScheduledClose() {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  customElements.define('ots-definition-popover', OtsDefinitionPopover);
})();
